/**
 * LOADING-TRUTH-FIX (S3) — einheitliche Lade-Zustandsmaschine fuer
 * Dashboard-Ressourcen.
 *
 * Stati je Ressource/Feld:
 *   IDLE → LOADING → LIVE_DATA | EMPTY_PROVEN | STALE | SOURCE_UNAVAILABLE | ERROR
 *
 * - EMPTY_PROVEN: autoritativ erfolgreiche, aber leere Antwort — NUR dann
 *   darf eine Zaehler-Flaeche "0" zeigen (NO_FALSE_ZERO).
 * - SOURCE_UNAVAILABLE: Timeout/Netzwerk/5xx — Quelle nicht erreichbar;
 *   Zaehler-Flaechen zeigen "—" statt Fake-0.
 * - STALE: spaet angekommene Antwort einer ueberholten Ladung (Late Arrival);
 *   ueberschreibt LIVE nie ohne Kennzeichnung (late=true).
 */

export type ResourceStatus =
  | "IDLE"
  | "LOADING"
  | "LIVE_DATA"
  | "EMPTY_PROVEN"
  | "STALE"
  | "SOURCE_UNAVAILABLE"
  | "ERROR";

export interface ResourceState<T = unknown> {
  status: ResourceStatus;
  /** Start des laufenden/letzten Ladeversuchs (epoch ms). */
  startedAt?: number;
  /** Erwarteter Timeout-Zeitpunkt des Ladeversuchs (epoch ms). */
  timeoutAt?: number;
  /** Abschluss (Erfolg oder Fehler) des letzten abgeschlossenen Versuchs. */
  finishedAt?: number;
  /** Wire-Quelle (Endpunkt), aus der geladen wurde. */
  source?: string;
  /** Zeitpunkt des letzten autoritativen Erfolgs (epoch ms). */
  lastSuccess?: number;
  /** Klassifizierung fuer STALE: Antwort kam nach einem neueren Versuch. */
  late?: boolean;
  data?: T;
  error?: string;
}

export const JFETCH_DEFAULT_TIMEOUT_MS = 15_000;

/** Klassifizierter Timeout-Fehler aus jfetch (S3). */
export class ApiTimeoutError extends Error {
  readonly code = "TIMEOUT" as const;
  readonly timeoutMs: number;
  constructor(path: string, timeoutMs: number) {
    super(`Timeout nach ${timeoutMs}ms: ${path} (code=TIMEOUT)`);
    this.name = "ApiTimeoutError";
    this.timeoutMs = timeoutMs;
  }
}

export function isTimeoutError(err: unknown): err is ApiTimeoutError {
  return err instanceof ApiTimeoutError;
}

/**
 * Fehler-Klassifikation: Timeout, Netzwerkfehler (fetch TypeError) und
 * Gateway-Fehler (502/503/504) sind SOURCE_UNAVAILABLE; alles andere ERROR.
 */
export function classifyLoadError(err: unknown): "SOURCE_UNAVAILABLE" | "ERROR" {
  if (isTimeoutError(err)) return "SOURCE_UNAVAILABLE";
  if (err instanceof TypeError) return "SOURCE_UNAVAILABLE";
  if (
    typeof err === "object" &&
    err !== null &&
    "status" in err &&
    [502, 503, 504].includes((err as { status?: unknown }).status as number)
  ) {
    return "SOURCE_UNAVAILABLE";
  }
  return "ERROR";
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Reduziert einen Zustandsuebergang fuer eine Ressource. `late=true`
 * bedeutet: diese Antwort gehoert zu einem aelteren Versuch (Late Arrival)
 * und wird als STALE gekennzeichnet, statt LIVE zu ueberschreiben.
 */
export function reduceLoaded<T>(
  base: ResourceState<T> | undefined,
  outcome:
    | { kind: "loading"; startedAt: number; timeoutAt: number; source: string }
    | { kind: "success"; value: T; isEmpty: boolean; finishedAt: number; late: boolean }
    | { kind: "error"; error: unknown; finishedAt: number; late: boolean },
): ResourceState<T> {
  if (outcome.kind === "loading") {
    return {
      ...base,
      status: "LOADING",
      startedAt: outcome.startedAt,
      timeoutAt: outcome.timeoutAt,
      source: outcome.source,
      late: undefined,
      error: undefined,
    };
  }
  if (outcome.kind === "success") {
    if (outcome.late) {
      return {
        ...base,
        status: "STALE",
        finishedAt: outcome.finishedAt,
        late: true,
        data: outcome.value,
      };
    }
    return {
      ...base,
      status: outcome.isEmpty ? "EMPTY_PROVEN" : "LIVE_DATA",
      finishedAt: outcome.finishedAt,
      late: undefined,
      data: outcome.value,
      error: undefined,
      lastSuccess: outcome.finishedAt,
    };
  }
  return {
    ...base,
    status: classifyLoadError(outcome.error),
    finishedAt: outcome.finishedAt,
    late: outcome.late || undefined,
    error: errorMessage(outcome.error),
  };
}
